'use client';

import type { ReactNode } from 'react';
import ProductCard from '@repo/ui/src/components/ContentGrid/ProductCard';
import RecipeCard from '@repo/ui/src/components/ContentGrid/RecipeCard';
import LearnCard from '@repo/ui/src/components/ContentGrid/LearnCard';
import Product from '@models/product';

export type SearchResultType = 'Product' | 'Recipe' | 'Learn';

interface ImageAsset {
  name: string;
  endpoint: string;
  defaultHost: string;
  url?: string;
}

interface SearchHit {
  __typename?: string;
  _meta?: { deliveryId?: string; deliveryKey?: string };
  // Product fields
  title?: string;
  images?: Array<ImageAsset>;
  productData?: Record<string, unknown>;
  shop?: { price?: number; calculatedPrice?: number };
  ratingSummary?: { average?: number; count?: number };
  // Recipe fields
  recipeMetaSection?: {
    title: string;
    description?: string;
    shortDescription?: string;
    complexity?: string;
  };
  assets?: Array<ImageAsset>;
  // Learn fields
  headerAsset?: Array<{ image?: { name?: string; endpoint?: string; defaultHost?: string } }>;
}

interface Props {
  result: SearchHit;
  searchType: SearchResultType | string;
}

const renderProduct = (result: SearchHit): ReactNode => {
  const product = new Product({
     
    ...(result as any),
    shop: {
      price: result.shop?.calculatedPrice ?? result.shop?.price,
      originalPrice: result.shop?.price,
    },
  });
  return (
    <ProductCard
      product={product}
      vip={false}
      className="border-l border-t bg-white"
      itemsPerRow={5}
    />
  );
};

const renderRecipe = (result: SearchHit): ReactNode => (
  <div className="bg-white h-full">
    <RecipeCard
       
      _meta={result._meta as any}
      recipeMetaSection={result.recipeMetaSection!}
      assets={result.assets ?? []}
      ratingSummary={result.ratingSummary as { average: number; count: number }}
    />
  </div>
);

const renderLearn = (result: SearchHit): ReactNode => (
  <div className="bg-white h-full">
    <LearnCard
       
      _meta={result._meta as any}
      title={result.title!}
      headerAsset={
        result.headerAsset
          ?.map((h) => h.image)
          .filter((img): img is NonNullable<typeof img> => Boolean(img)) ?? []
      }
    />
  </div>
);

const CARD_RENDERERS: Partial<Record<string, (result: SearchHit) => ReactNode>> = {
  Product: renderProduct,
  Recipe: renderRecipe,
  RecipeListItem: renderRecipe,
  Learn: renderLearn,
  LearningContent: renderLearn,
};

const SearchResultCard = ({ result, searchType }: Props) => {
  const resolvedType = searchType || result.__typename;
  return resolvedType ? (CARD_RENDERERS[resolvedType]?.(result) ?? null) : null;
};

export default SearchResultCard;
